import { atom, SetterOrUpdater } from "recoil";
import { boardMissionRowKey } from "@/widgets/production-floor/utils/board-mission-barcode.utils";
import { IStatusesBoardsMissions } from "@/widgets/production-floor/interfaces/production-floor-data-response";

export interface IDirectQrProcessingRow {
  boardMissionId: string;
  productType: string;
  startedAt: number;
}

export type DirectQrProcessingRowsMap = Record<string, IDirectQrProcessingRow>;

export const directQrProcessingRowsState = atom<DirectQrProcessingRowsMap>({
  key: "directQrProcessingRowsState",
  default: {},
});

export const markDirectQrProcessingRow = (
  setRows: SetterOrUpdater<DirectQrProcessingRowsMap>,
  boardMissionId: string,
  productType: string
) => {
  const key = boardMissionRowKey(boardMissionId, productType);
  setRows((prev) => ({
    ...prev,
    [key]: {
      boardMissionId: String(boardMissionId),
      productType: String(productType),
      startedAt: Date.now(),
    },
  }));
};

export const clearDirectQrProcessingRow = (
  setRows: SetterOrUpdater<DirectQrProcessingRowsMap>,
  boardMissionId: string,
  productType?: string | null
) => {
  const id = String(boardMissionId);
  const keysToTry = new Set([
    id,
    boardMissionRowKey(id, productType ?? ""),
    boardMissionRowKey(id, ""),
  ]);

  setRows((prev) => {
    const next = { ...prev };
    let changed = false;

    Object.keys(prev).forEach((key) => {
      if (keysToTry.has(key) || prev[key]?.boardMissionId === id) {
        delete next[key];
        changed = true;
      }
    });

    return changed ? next : prev;
  });
};

export const isDirectQrProcessingRow = (
  rows: DirectQrProcessingRowsMap,
  boardMissionId: string,
  productType: string
): boolean => {
  const id = String(boardMissionId);
  return Boolean(rows[id] || rows[boardMissionRowKey(id, productType)]);
};

/** After board refresh, server row data owns lock state (`isFreezedStation`). */
export const syncDirectQrProcessingRowsWithBoardMissions = (
  setRows: SetterOrUpdater<DirectQrProcessingRowsMap>,
  boardCollections: IStatusesBoardsMissions[]
) => {
  const boardMissionIdsInServerData = new Set<string>();

  boardCollections?.forEach((collection) => {
    collection.boardMissions?.forEach((board) => {
      boardMissionIdsInServerData.add(String(board.id));
    });
  });

  setRows((prev) => {
    if (!Object.keys(prev).length) {
      return prev;
    }

    const next = { ...prev };
    let changed = false;

    Object.values(prev).forEach((entry) => {
      if (boardMissionIdsInServerData.has(entry.boardMissionId)) {
        const key = boardMissionRowKey(
          entry.boardMissionId,
          entry.productType
        );
        if (key in next) {
          delete next[key];
          changed = true;
        }
      }
    });

    return changed ? next : prev;
  });
};
